import { ARCHIVED_BANNER_HEIGHT } from "components/archived-project-banner";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Tooltip,
  message
} from "antd";
import type { CheckboxProps } from "antd";

import { useForm } from "antd/lib/form/Form";
import {
  dummyColumnDef,
  IntegrationNameCellRenderer,
  integrationTypeCellRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { GridLoadingIndicator } from "components/widgets";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  getOAuthUrl,
  saveACCIntegrationConfig
} from "services/integration-service";
import {
  IntegrationType,
  SCHEDULE_SOURCE_SYSTEMS,
  SUBMITTAL_SOURCE_SYSTEMS,
  canOpenIntegrationDetails,
  getOpcAccounts
} from "pages/subscription-settings/integrations-tab-new/utils";
import {
  addedByColumnDef,
  accountNameHeaderComponentParams,
  AlreadyIntegratedOptionLabel
} from "pages/subscription-settings/common-components";
import { useIntegrationAuthProcess } from "hooks/integration-hooks";
import ProjectIntegrationListMessage from "components/widgets/project-integration-list-message";
import { useHistory, useParams } from "react-router";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import {
  ErrorMessages,
  ESubmittalMgmtModes,
  IntegrationAuthProcessStatuses,
  IntegrationSystemNameMap,
  OPC_INTEGRATION_SYSTEM,
  ProjectPermissionEnum
} from "../../../constants";
import { ProjectIntegrationsTabSharedContext } from "./project-integrations-tab-context";

const { Option, OptGroup } = Select;

function emptyProjectIntegrationMessage(input: {
  canOpenAddIntegration: boolean;
  hasSubscriptionConfigs: boolean;
  projectConfigCount: number;
  onConfigure: () => void;
}) {
  if (!input.canOpenAddIntegration || !input.hasSubscriptionConfigs)
    return null;
  if (input.projectConfigCount !== 0) return null;
  return <ProjectIntegrationListMessage onConfigureClick={input.onConfigure} />;
}

export default function ProjectIntegrationsList() {
  const history = useHistory();

  const {
    tokenRetrievalState,
    gqlClientForProject,
    tokenContents,
    isFeatureFlagEnabled,
    projectDetails,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);

  const { dispatch, sharedState } = useContext(
    ProjectIntegrationsTabSharedContext
  );

  const gridRef = useRef<AgGridReact>(null);
  const [form] = useForm();

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  const [addingIntegration, setAddingIntegration] = useState(false);
  const [isSubIntConfigured, setSubIntConfigured] = useState(false);

  const canAddIntegration = !isPermissionNotGrantted(
    ProjectPermissionEnum.addProjectIntegration,
    tokenContents?.role ?? ""
  );

  const { initAuthProcess } = useIntegrationAuthProcess(
    "project",
    gqlClientForProject
  );

  const [openAddIntegrationPanel, setOpenAddIntegrationPanel] = useState(false);

  const [checked, setChecked] = useState(false);
  const { projectId } = useParams() as any;

  const showOpc = isFeatureFlagEnabled("OPC_INTEGRATION_V1");
  // Workflow-mode projects only add schedule (OPC) integrations — never
  // submittal sources. Integration mode keeps Submittals (+ Schedules if OPC).
  const schedulesOnlySourceOptions =
    projectDetails?.mode === ESubmittalMgmtModes.WORKFLOW_MODE && showOpc;

  const hasOpcIntegration = useMemo(() => {
    return sharedState?.projectIntegrationConfigs?.some(
      (config: any) => config?.integration?.system === OPC_INTEGRATION_SYSTEM
    );
  }, [sharedState?.projectIntegrationConfigs]);

  const integratedSubmittalSystems = useMemo(() => {
    const systems = new Set<string>();
    sharedState?.projectIntegrationConfigs?.forEach((config: any) => {
      const system = config?.integration?.system;
      if (system && SUBMITTAL_SOURCE_SYSTEMS.includes(system)) {
        systems.add(system);
      }
    });
    return systems;
  }, [sharedState?.projectIntegrationConfigs]);

  const hasSubmittalIntegration = integratedSubmittalSystems.size > 0;

  // Add Integration stays available until each available module has a source:
  // submittals (when in integration mode) and schedules (when OPC is on).
  const isFullyIntegrated = schedulesOnlySourceOptions
    ? !!hasOpcIntegration
    : showOpc
      ? hasSubmittalIntegration && !!hasOpcIntegration
      : hasSubmittalIntegration;

  const fullyIntegratedMessage = schedulesOnlySourceOptions
    ? "Project is already integrated with source for Schedules."
    : showOpc
      ? "Project is already integrated with source for Submittals and Schedules."
      : "Project is already integrated with source for Submittals.";

  const addIntegrationDisabledReason = !canAddIntegration
    ? isProjectArchived
      ? undefined
      : ErrorMessages.PermissionNotGranted
    : isFullyIntegrated
      ? fullyIntegratedMessage
      : "";

  const canOpenAddIntegration = canAddIntegration && !isFullyIntegrated;

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      filter: "agSetColumnFilter",
      autoHeight: false,
      resizable: true,
      flex: 1,
      minWidth: 100,
      comparator: comparatorWithEmptyAtBottom,
      cellClass: "ciq-cell-v-center"
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        headerName: "Integration Name",
        headerTooltip: "Integration Name",
        valueGetter: ({ data }: any) => data?.properties?.INTEGRATION_NAME,
        cellRenderer: IntegrationNameCellRenderer
      },
      {
        headerName: "Source Application",
        headerTooltip: "Source Application",
        field: "integration.system",
        filterParams: {
          valueFormatter: (params: any) =>
            IntegrationSystemNameMap[params.value] || params.value
        },
        cellRenderer: integrationTypeCellRenderer
      },
      {
        headerName: "Account Name",
        headerComponentParams: accountNameHeaderComponentParams,
        // Account lives on the SUBSCRIPTION config — it is authorized once and
        // shared by every project under it. Vendors disagree on the key
        // (Autodesk/OPC say ACCOUNT, Procore says COMPANY), hence the chain.
        // Blank is correct for file-based rows, which never pick an account.
        valueGetter: ({ data }: any) => {
          return (
            data.subscriptionProperties?.PROCORE_COMPANY_NAME ||
            data.subscriptionProperties?.AUTODESK_ACCOUNT_NAME ||
            data.properties?.OPC_WORKSPACE_NAME ||
            data.subscriptionProperties?.OPC_WORKSPACE_NAME
          );
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "Project Name in Source",
        headerTooltip: "Project Name in Source",
        // Project lives on the PROJECT config. Consistent `<SYSTEM>_PROJECT_NAME`
        // naming here, unlike the account keys above.
        valueGetter: ({ data }: any) => {
          return (
            data.properties?.PROCORE_PROJECT_NAME ||
            data.properties?.AUTODESK_PROJECT_NAME ||
            data.properties?.OPC_PROJECT_NAME
          );
        },
        cellRenderer: truncatedTextCellRenderer
      },
      addedByColumnDef
    ];
  }, []);

  const availableIntegrationOptions = useMemo(() => {
    const activeSourceType = schedulesOnlySourceOptions
      ? ["OPC"]
      : showOpc
        ? ["AUTODESK", "PROCORE", "BIM360", "OPC"]
        : ["AUTODESK", "PROCORE", "BIM360"];

    const findSubscriptionConfig = (system: string) =>
      sharedState?.subscriptionIntegrationConfigs?.find(
        (sIntgConfig: any) => sIntgConfig.integration?.system === system
      ) || null;

    // Always surface these under the dropdown even if GraphQL omits them from
    // `integrations` or subscription configs are still empty (shows the
    // Subscription Settings hint when not configured).
    const ensureSystemOption = (options: any[], system: string) => {
      if (!activeSourceType.includes(system)) return options;
      if (options.some((o) => o.system === system)) return options;
      const fromApi = sharedState?.availableIntegrations?.find(
        (i: any) => i.system === system
      );
      const valueBySystem: Record<string, number> = {
        AUTODESK: IntegrationType.AUTODESK,
        PROCORE: IntegrationType.PROCORE,
        BIM360: IntegrationType.BIM360,
        [OPC_INTEGRATION_SYSTEM]: IntegrationType.OPC
      };
      return [
        ...options,
        {
          value: fromApi?.id ?? valueBySystem[system],
          label: IntegrationSystemNameMap[system],
          system,
          subscriptionIntgConfig: findSubscriptionConfig(system)
        }
      ];
    };

    const ensureRequiredOptions = (options: any[]) => {
      let next = options;
      if (!schedulesOnlySourceOptions) {
        next = ensureSystemOption(next, "AUTODESK");
        next = ensureSystemOption(next, "PROCORE");
        next = ensureSystemOption(next, "BIM360");
      }
      if (showOpc) {
        next = ensureSystemOption(next, OPC_INTEGRATION_SYSTEM);
      }
      return next;
    };

    const toGroupedOrFlat = (opts: any[]) => {
      if (schedulesOnlySourceOptions) {
        return [
          {
            label: "Schedules",
            options: opts.filter((o: any) =>
              SCHEDULE_SOURCE_SYSTEMS.includes(o.system)
            )
          }
        ].filter((g) => g.options.length > 0);
      }
      // Flat list when OPC is off — no Submittals/Schedules group headers.
      if (!showOpc) {
        return opts.filter((o: any) =>
          SUBMITTAL_SOURCE_SYSTEMS.includes(o.system)
        );
      }
      return [
        {
          label: "Submittals",
          options: opts.filter((o: any) =>
            SUBMITTAL_SOURCE_SYSTEMS.includes(o.system)
          )
        },
        {
          label: "Schedules",
          options: opts.filter((o: any) =>
            SCHEDULE_SOURCE_SYSTEMS.includes(o.system)
          )
        }
      ].filter((g) => g.options.length > 0);
    };

    if (
      !sharedState?.availableIntegrations ||
      !sharedState?.subscriptionIntegrationConfigs?.length
    ) {
      return toGroupedOrFlat(ensureRequiredOptions([]));
    }

    const opts = ensureRequiredOptions(
      sharedState.availableIntegrations
        .filter((opt: any) => activeSourceType.includes(opt?.system))
        .sort((intA: any, intB: any) => (intA?.system > intB?.system ? 1 : -1))
        .map((availableIntegration: any) => {
          return {
            value: availableIntegration.id,
            label: IntegrationSystemNameMap[availableIntegration.system],
            system: availableIntegration.system,
            subscriptionIntgConfig: findSubscriptionConfig(
              availableIntegration.system
            )
          };
        })
    );

    return toGroupedOrFlat(opts);
  }, [
    sharedState?.availableIntegrations,
    sharedState.subscriptionIntegrationConfigs,
    showOpc,
    schedulesOnlySourceOptions
  ]);

  const isGroupedIntegrationOptions = showOpc;

  const flatIntegrationOptions = useMemo(() => {
    if (!isGroupedIntegrationOptions) {
      return availableIntegrationOptions;
    }
    return availableIntegrationOptions.flatMap((g: any) => g.options);
  }, [availableIntegrationOptions, isGroupedIntegrationOptions]);

  const selectedIntegrationType = Form.useWatch("integrationType", form);
  const selectedIntegrationMethod = Form.useWatch("integrationMethod", form);

  const selectedSystem = useMemo(() => {
    if (
      selectedIntegrationType === undefined ||
      selectedIntegrationType === null
    ) {
      return undefined;
    }
    return (
      flatIntegrationOptions.find(
        (opt: any) => String(opt.value) === String(selectedIntegrationType)
      )?.system ||
      sharedState?.availableIntegrations?.find(
        (i: any) => String(i.id) === String(selectedIntegrationType)
      )?.system
    );
  }, [
    flatIntegrationOptions,
    selectedIntegrationType,
    sharedState?.availableIntegrations
  ]);

  const isOpcSelected = selectedSystem === OPC_INTEGRATION_SYSTEM;
  const isProcoreSelected = selectedSystem === "PROCORE";
  const isAutodeskSelected = selectedSystem === "AUTODESK";
  const isBim360Selected = selectedSystem === "BIM360";

  const opcAccounts = useMemo(
    () => getOpcAccounts(sharedState?.subscriptionIntegrationConfigs),
    [sharedState?.subscriptionIntegrationConfigs]
  );

  const opcAccountOptions = useMemo(
    () =>
      opcAccounts
        .map((account) => ({ label: account.name, value: account.id }))
        .sort((a1, a2) =>
          a1.label.toLowerCase() > a2.label.toLowerCase() ? 1 : -1
        ),
    [opcAccounts]
  );

  useEffect(() => {
    if (isProcoreSelected) {
      const foundProcoreConfig = sharedState.systemIntegrationConfigs.find(
        (config: any) => {
          return config.system === IntegrationType[IntegrationType.PROCORE];
        }
      );

      form.setFieldsValue({
        root_url:
          foundProcoreConfig.integration_config_properties.PROCORE_ROOT_URL,
        login_url:
          foundProcoreConfig.integration_config_properties.PROCORE_LOGIN_URL
      });
    }
  }, [
    form,
    selectedIntegrationType,
    sharedState.systemIntegrationConfigs,
    isProcoreSelected
  ]);

  const closeAddPanel = () => {
    setOpenAddIntegrationPanel(false);
    form.resetFields();
  };

  const onManageIntegrationClick = async (data: any) => {
    dispatch({
      type: "SET_SHARED_STATE",
      payload: {
        activeView: "details",
        system: data.integration.system,
        additionalData: data
      }
    });
  };

  const getIntegrationSourceType = (integrationType: IntegrationType) => {
    switch (integrationType) {
      case IntegrationType.AUTODESK:
        return "autodesk";
      // saveConfig (the only caller) is only invoked for AUTODESK/BIM360; CMIC is not an active source type and never reaches here
      /* v8 ignore next 2 */
      case IntegrationType.CMIC:
        return "cmic";
      case IntegrationType.BIM360:
        return "bim360";
      // saveConfig only passes AUTODESK/BIM360 (both handled above); the default arm is unreachable
      /* v8 ignore next 2 */
      default:
        return "";
    }
  };

  const saveConfig = async (values: any) => {
    const payload: any = {
      // values comes from Form onFinish (post-validation); integrationName is a required, non-empty field, so the ?. null and ?? "" fallback arms never fire
      /* v8 ignore next */
      integration_name: values?.integrationName ?? ""
    };

    const integrationSourceType = getIntegrationSourceType(
      values.integrationType
    );
    const integrationMethod = selectedIntegrationMethod ?? "file";

    const saveResponse = await saveACCIntegrationConfig(
      payload,
      tokenRetrievalState.token,
      integrationMethod,
      integrationSourceType
    );
    if (saveResponse.success) {
      if (checked) {
        window.open(`/project/${projectId}/submittals`, "_blank");
      }
      setOpenAddIntegrationPanel(false);
      setAddingIntegration(false);
    }
  };

  const onFormSubmit = async (values: any) => {
    // Project-level OPC reuses subscription authorization — skip login and go
    // straight to workspace/project selection (same pattern as Procore).
    if (isOpcSelected) {
      closeAddPanel();
      dispatch({
        type: "SET_SHARED_STATE",
        payload: {
          activeView: "configure-initial",
          system: OPC_INTEGRATION_SYSTEM,
          additionalData: {
            newIntegrationName: values.integrationName,
            opcAccountId: values.opcAccountId
          }
        }
      });
      return;
    }

    setAddingIntegration(true);

    if (isAutodeskSelected) {
      if (selectedIntegrationMethod === "file") {
        saveConfig(values);
        return;
      }
      if (!selectedSystem) {
        setAddingIntegration(false);
        message.error("Select a valid source application.");
        return;
      }
      const OAuthURLResponse = await getOAuthUrl(
        selectedSystem,
        {},
        tokenRetrievalState?.token
      );

      setAddingIntegration(false);

      if (OAuthURLResponse.success) {
        const authResponse: any = await initAuthProcess(OAuthURLResponse.data);
        if (
          authResponse?.oauth_status?.status ===
          IntegrationAuthProcessStatuses.PROCESSED
        ) {
          setOpenAddIntegrationPanel(false);
          dispatch({
            type: "SET_SHARED_STATE",
            payload: {
              activeView: "configure-initial",
              system: authResponse.source_type.toUpperCase(),
              additionalData: {
                newIntegrationName: form.getFieldValue("integrationName")
              }
            }
          });
        } else {
          const error = authResponse.output?.error || "Authorization failed";
          message.error(error);
        }
      }
    }

    if (isProcoreSelected) {
      dispatch({
        type: "SET_SHARED_STATE",
        payload: {
          activeView: "configure-initial",
          system: IntegrationType[IntegrationType.PROCORE],
          additionalData: {
            newIntegrationName: form.getFieldValue("integrationName")
          }
        }
      });
    }

    if (isBim360Selected) {
      saveConfig(values);
    }
  };

  const onChange: CheckboxProps["onChange"] = (e) => {
    setChecked(e.target.checked);
  };

  const headerItems = useMemo(() => {
    const items: JSX.Element[] = [];
    items.push(
      <Tooltip
        key="add-integration"
        title={addIntegrationDisabledReason || undefined}
      >
        <span>
          <Button
            type="primary"
            onClick={() => {
              setOpenAddIntegrationPanel(true);
            }}
            disabled={!canOpenAddIntegration || addingIntegration}
            data-testid="add-integration-button"
          >
            Add Integration
          </Button>
        </span>
      </Tooltip>
    );

    return items;
  }, [addingIntegration, canOpenAddIntegration, addIntegrationDisabledReason]);

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const isOptionDisabled = (option: any) => {
    if (option.system === OPC_INTEGRATION_SYSTEM) {
      return !option.subscriptionIntgConfig || hasOpcIntegration;
    }
    // Any existing project submittal integration locks out the other
    // submittal sources (integrated one shows "Already integrated.").
    if (
      hasSubmittalIntegration &&
      SUBMITTAL_SOURCE_SYSTEMS.includes(option.system)
    ) {
      return true;
    }
    if (option.label === IntegrationSystemNameMap.PROCORE) {
      return !option.subscriptionIntgConfig;
    }
    return false;
  };

  const subscriptionSetupHint =
    "Go to Subscription Settings to add this integration.";

  const renderOptionLabel = (option: any) => {
    if (option.system === OPC_INTEGRATION_SYSTEM && hasOpcIntegration) {
      return (
        <AlreadyIntegratedOptionLabel
          label={option.label}
          message="Already integrated."
        />
      );
    }

    if (
      hasSubmittalIntegration &&
      SUBMITTAL_SOURCE_SYSTEMS.includes(option.system)
    ) {
      if (integratedSubmittalSystems.has(option.system)) {
        return (
          <AlreadyIntegratedOptionLabel
            label={option.label}
            message="Already integrated."
          />
        );
      }
      // Sibling submittal sources are locked; keep label muted even if
      // dropdown theme styles fight ant-select disabled color.
      return <span className="!text-black/25">{option.label}</span>;
    }

    if (
      option.subscriptionIntgConfig ||
      option.label === IntegrationSystemNameMap.BIM360 ||
      option.label === IntegrationSystemNameMap.AUTODESK
    ) {
      return option.label;
    }

    return (
      <div className="min-w-0 max-w-full">
        <div className="text-gray-400 truncate">{option.label}</div>
        <Tooltip title={subscriptionSetupHint}>
          <div className="text-gray-600 truncate pointer-events-auto">
            Go to{" "}
            <a
              href="/settings/integrations"
              className="font-medium workflow-document-upload"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                history.push("/settings/integrations");
              }}
            >
              Subscription Settings
            </a>{" "}
            to add this integration.
          </div>
        </Tooltip>
      </div>
    );
  };

  function projectIntegrationsBody() {
    return (
      <div>
        <div>
          <CiqLogPageHeader
            entityName="Integrations"
            titleParam={{
              title: "Integrations",
              totalCount: sharedState?.projectIntegrationConfigs?.length || 0,
              filterStats: statsData
            }}
            items={headerItems}
          />
          <div className="h-[500px] ag-theme-alpine relative">
            {emptyProjectIntegrationMessage({
              canOpenAddIntegration,
              hasSubscriptionConfigs: Boolean(
                sharedState.subscriptionIntegrationConfigs
              ),
              projectConfigCount:
                sharedState?.projectIntegrationConfigs?.length ?? -1,
              onConfigure: () => setOpenAddIntegrationPanel(true)
            })}
            <AgGridReact<any>
              ref={gridRef}
              onFirstDataRendered={() => {
                gridRef?.current?.api?.sizeColumnsToFit();
                gridRef?.current?.api?.resetRowHeights();
              }}
              rowData={sharedState?.projectIntegrationConfigs}
              columnDefs={columnDefs} // Column Defs for Columns
              defaultColDef={defaultColDef} // Default Column Properties
              rowHeight={47}
              animateRows={false} // Optional - set to 'true' to have rows animate when sorted
              loadingOverlayComponent={GridLoadingIndicator}
              suppressDragLeaveHidesColumns
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              suppressContextMenu
              suppressRowDrag
              suppressMovableColumns
              context={{
                onManageIntegrationClick
              }}
              getRowClass={(params) =>
                canOpenIntegrationDetails(params.data)
                  ? "integration-details-clickable-row"
                  : undefined
              }
              onRowClicked={(event) => {
                if (canOpenIntegrationDetails(event.data)) {
                  onManageIntegrationClick(event.data);
                }
              }}
              suppressNoRowsOverlay
              onFilterChanged={onFiltersApplied}
            />
          </div>
        </div>

        <Modal
          title="ADD INTEGRATION"
          width={420}
          style={{
            right: 0,
            bottom: 0,
            top: 40,
            padding: 0,
            position: "absolute",
            overflow: "hidden"
          }}
          styles={{
            body: {
              height: `calc(100vh - 92px - ${ARCHIVED_BANNER_HEIGHT})`,
              padding: "10px 0px 10px 0px"
            }
          }}
          footer={null}
          open={openAddIntegrationPanel}
          onCancel={closeAddPanel}
          destroyOnHidden
          mask={{ closable: false }}
          keyboard={false}
        >
          <div className="px-3 pb-3 h-full">
            <Form
              form={form}
              layout="vertical"
              className="h-full overflow-y-auto"
              onFinish={onFormSubmit}
              disabled={addingIntegration}
            >
              <div className="h-full flex flex-col">
                <div className="grow">
                  <div>
                    <Form.Item
                      name="integrationType"
                      label="Source Application"
                      required
                      rules={[
                        {
                          required: true,
                          message: "Select Source Application"
                        }
                      ]}
                    >
                      <Select
                        placeholder="Select Source Application"
                        optionLabelProp="label"
                        classNames={{
                          popup: { root: "integration-source-select-dropdown" }
                        }}
                        notFoundContent={
                          <div>
                            Go to{" "}
                            <Button
                              size="small"
                              type="text"
                              className="font-medium workflow-document-upload"
                              onClick={() => {
                                history.push("/settings/integrations");
                              }}
                            >
                              Subscription settings
                            </Button>{" "}
                            to add integrations
                          </div>
                        }
                        onChange={(value) => {
                          setChecked(false);
                          form.setFieldValue("opcAccountId", undefined);

                          const integration: any = flatIntegrationOptions.find(
                            (opt: any) => opt.value === value
                          );

                          if (
                            !integration.subscriptionIntgConfig &&
                            integration.value === 1
                          ) {
                            form.setFieldValue("integrationMethod", "file");
                          } else {
                            form.setFieldValue("integrationMethod", null);
                          }

                          setSubIntConfigured(
                            integration?.subscriptionIntgConfig
                          );
                        }}
                      >
                        {isGroupedIntegrationOptions
                          ? availableIntegrationOptions.map((group: any) => (
                              <OptGroup key={group.label} label={group.label}>
                                {group.options.map((option: any) => (
                                  <Option
                                    key={option.value}
                                    value={option.value}
                                    label={option.label}
                                    disabled={isOptionDisabled(option)}
                                  >
                                    {renderOptionLabel(option)}
                                  </Option>
                                ))}
                              </OptGroup>
                            ))
                          : availableIntegrationOptions.map((option: any) => (
                              <Option
                                key={option.value}
                                value={option.value}
                                label={option.label}
                                disabled={isOptionDisabled(option)}
                              >
                                {renderOptionLabel(option)}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </div>
                  {isOpcSelected && (
                    <div>
                      <Form.Item
                        name="opcAccountId"
                        label="Subscription Integration Name"
                        required
                        rules={[
                          {
                            required: true,
                            message: "Select Subscription Integration Name"
                          }
                        ]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select Subscription Integration Name"
                          options={opcAccountOptions}
                          onChange={(value) => {
                            const account = opcAccounts.find(
                              (acc) => acc.id === value
                            );
                            form.setFields([
                              {
                                name: "integrationName",
                                value: account?.name,
                                errors: []
                              }
                            ]);
                          }}
                        />
                      </Form.Item>
                    </div>
                  )}
                  <div>
                    <Form.Item
                      name="integrationName"
                      label={
                        isOpcSelected
                          ? "Project Integration Name"
                          : "Integration Name"
                      }
                      required
                      rules={[
                        {
                          required: true,
                          message: isOpcSelected
                            ? "Enter Project Integration Name"
                            : "Enter Integration Name"
                        }
                      ]}
                    >
                      <Input
                        placeholder={
                          isOpcSelected
                            ? "Enter Project Integration Name"
                            : "Enter Integration Name"
                        }
                      />
                    </Form.Item>
                  </div>
                  {(isAutodeskSelected || isProcoreSelected) && <Divider />}
                  {isAutodeskSelected && (
                    <Form.Item
                      name="integrationMethod"
                      label="Integration Method"
                      className="-mt-1"
                      required
                      rules={[
                        {
                          required: true,
                          message: "Select integration method"
                        }
                      ]}
                    >
                      {/* antd v6 gives the radio `align-self: center`, which
                          centres the dot against the whole two-line label
                          instead of its title (23px too low). Pin it to the top
                          and nudge it down 4px to sit level with the title. */}
                      <Radio.Group className="grid grid-rows-2  px-1 pt-2 space-y-2 [&_.ant-radio]:!self-start [&_.ant-radio]:!mt-1">
                        <Radio value="api" disabled={!isSubIntConfigured}>
                          <div className="font-medium text-sm text-ciq-gray-900">
                            {" "}
                            API-Based (Recommended)
                          </div>
                          <div className="mt-1 font-normal text-xs text-black text-opacity-88 leading-[22px]">
                            Sync data automatically by connecting your Autodesk
                            Account.
                          </div>
                        </Radio>
                        <Radio value="file">
                          <div className="font-medium text-sm text-ciq-gray-900">
                            File-Based (If API Access not available)
                          </div>
                          <div className="mt-1 font-normal  text-xs text-black text-opacity-90 leading-[22px]">
                            Manually upload .XLSX or .CSV Exports from your
                            Autodesk Forma project.
                          </div>
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  )}
                  {isProcoreSelected && (
                    <div className="pb-2">
                      <div className="mt-2 mb-1 text-xs">
                        <div className="font-semibold mb-1">
                          Contact your Procore Company Admin to follow the below
                          steps before proceeding here:
                        </div>
                        <ol className="m-0 pl-4 space-y-0.5">
                          <li>Go to Procore App Management.</li>
                          <li>Select ConstructivIQ App.</li>
                          <li>Go to Permissions.</li>
                          <li>
                            Add the project which you wish to integrate under{" "}
                            <br />
                            &apos;Permitted Projects&apos;.
                          </li>
                        </ol>
                      </div>
                    </div>
                  )}
                </div>
                {isAutodeskSelected && (
                  <div>
                    {selectedIntegrationMethod === "api" && (
                      <div className="p-1 flex flex-col text-black text-opacity-80">
                        <p className="font-semibold text-xs">Next Steps:</p>
                        <p className="font-normal text-xs">
                          Clicking “Next” will redirect you to log in to
                          Autodesk.
                        </p>
                        <div className="font-normal italic text-xs">
                          <p>
                            To log in, use the credentials of an Autodesk Forma
                            Account Admin who is part of at least one Autodesk®
                            Project.
                          </p>
                          <p>
                            Use credentials of a user with Submittal Manager
                            access in the Autodesk Forma project to ensure all
                            Submittal get synced.
                          </p>
                        </div>
                        <p className="font-normal text-[10px]">
                          Read the{" "}
                          <a
                            href="https://apps.autodesk.com/BIM360/en/Detail/HelpDoc?appId=4545831120489722584&appLang=en&os=Web"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Help Document
                          </a>{" "}
                          for more details.
                        </p>
                      </div>
                    )}
                    {selectedIntegrationMethod === "file" && (
                      <div className="p-1 pb-2 flex flex-col text-xs leading-[14.52px] text-black text-opacity-80">
                        <p className="font-semibold">Next Steps:</p>
                        <ul className="m-0 pl-6">
                          <li>Export Submittal Log from Autodesk Forma.</li>
                          <li>
                            Import the file into the Submittals Module in
                            ConstructivIQ.
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {isBim360Selected && (
                  <div className="p-1 pb-2 flex flex-col text-xs leading-[14.52px] text-black text-opacity-80">
                    <p className="font-semibold">Next Steps:</p>
                    <ul className="m-0 pl-6">
                      <li>Export Submittal Log from BIM360.</li>
                      <li>
                        Import the file into the Submittals Module in
                        ConstructivIQ.
                      </li>
                    </ul>
                  </div>
                )}
                {isOpcSelected && (
                  <div className="p-1 flex flex-col text-black text-opacity-80">
                    <p className="font-semibold text-xs">Next Steps:</p>
                    <p className="font-normal text-xs">
                      Clicking “Next” will use your subscription OPC
                      authorization to select a workspace and project.
                    </p>
                  </div>
                )}
                <div>
                  <Divider className="mx-0 mt-1 mb-4" />
                  <div className="flex justify-end items-center">
                    {(isBim360Selected ||
                      selectedIntegrationMethod === "file") && (
                      <Checkbox
                        disabled={addingIntegration}
                        onChange={onChange}
                        className="w-full"
                      >
                        Open Submittals on Save.
                      </Checkbox>
                    )}
                    <div className="flex space-x-4 justify-end">
                      <Button
                        htmlType="reset"
                        className="px-5"
                        onClick={closeAddPanel}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="px-5"
                        disabled={addingIntegration}
                        loading={addingIntegration}
                      >
                        {selectedIntegrationMethod === "file" ||
                        isBim360Selected
                          ? "Save"
                          : "Next"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </Modal>
      </div>
    );
  }
  return projectIntegrationsBody();
}
